/**
 * Application root: providers, accessible layout landmarks, and routing.
 */

import { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
} from "react-router-dom";

import { usePreferences } from "@/app/usePreferences";
import { CreatePage } from "@/pages/CreatePage";
import { GuardianPage } from "@/pages/GuardianPage";
import { HomePage } from "@/pages/HomePage";
import { ReaderPage } from "@/pages/ReaderPage";
import { registerOutboxAutoFlush, pendingCount } from "@/offline/outbox";
import { registerServiceWorker, type SwController } from "@/sw/registerSW";

/**
 * Release version baked into the bundle at image build time
 * (Docker ARG APP_VERSION -> VITE_APP_VERSION). Falls back to "dev" for the
 * local Vite dev server where no build arg is supplied.
 */
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "dev";
const GIT_SHA = import.meta.env.VITE_GIT_SHA ?? "";

function UpdateBanner({ controller }: { controller: SwController }) {
  return (
    <div
      role="status"
      className="kid-card"
      style={{ padding: "0.75rem 1rem", margin: "0.5rem 1rem" }}
    >
      A new version is ready.{" "}
      <button
        type="button"
        className="kid-button kid-button--secondary"
        style={{ minHeight: "2.5rem", fontSize: "1rem" }}
        onClick={controller.update}
      >
        Refresh
      </button>
    </div>
  );
}

function Header({ pending }: { pending: number }) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        padding: "1rem",
        backgroundColor: "var(--color-grape)",
        color: "var(--color-cloud)",
      }}
    >
      <Link
        to="/"
        style={{ color: "inherit", textDecoration: "none", fontSize: "1.5rem", fontWeight: 700 }}
      >
        📚 Creative Reading
      </Link>
      <nav aria-label="Main" style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
        {pending > 0 && (
          <span aria-label={`${pending} answers waiting to sync`} title="Answers waiting to sync">
            📦 {pending}
          </span>
        )}
        <NavLink to="/guardian" style={{ color: "inherit" }}>
          Grown-ups
        </NavLink>
      </nav>
    </header>
  );
}

function DyslexicToggle() {
  const { dyslexicFont, toggleDyslexicFont } = usePreferences();
  return (
    <button
      type="button"
      className="kid-button kid-button--secondary"
      style={{ minHeight: "2.75rem", fontSize: "1rem" }}
      aria-pressed={dyslexicFont}
      onClick={toggleDyslexicFont}
    >
      {dyslexicFont ? "✓ " : ""}Easy-read font
    </button>
  );
}

export function App() {
  const [swController, setSwController] = useState<SwController | null>(null);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    registerServiceWorker(setSwController);
    const cleanup = registerOutboxAutoFlush();
    const refresh = () => void pendingCount().then(setPending);
    refresh();
    const interval = window.setInterval(refresh, 15_000);
    window.addEventListener("online", refresh);
    return () => {
      cleanup();
      window.clearInterval(interval);
      window.removeEventListener("online", refresh);
    };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
      <Header pending={pending} />
      {swController && <UpdateBanner controller={swController} />}
      <main
        id="main-content"
        style={{
          flex: 1,
          width: "100%",
          maxWidth: "48rem",
          margin: "0 auto",
          padding: "1.5rem 1rem 3rem",
          boxSizing: "border-box",
        }}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/create" element={<CreatePage />} />
          <Route path="/book/:bookId" element={<ReaderPage />} />
          <Route path="/guardian" element={<GuardianPage />} />
        </Routes>
      </main>
      <footer
        style={{
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        <DyslexicToggle />
        <small
          style={{ opacity: 0.6, fontSize: "0.75rem" }}
          aria-label={`App version ${APP_VERSION}`}
          title={GIT_SHA ? `Build ${GIT_SHA}` : undefined}
        >
          v{APP_VERSION}
        </small>
      </footer>
    </div>
  );
}
